import { Box, Grid, TableContainer, Typography } from '@mui/material';
import Button from '../../../../components/button/Button';
import useProvidersModule from './hooks/useProvidersModule';
import DrawerManager from './components/drawers';
import DataTable from '../../../../components/DataTable/DataTable';
import { TEST_IDS } from './testIds';
import { useAppSelector } from '../../../../hooks/reduxHooks';
import { mainTableColumnTitles, NEW_PROVIDER_OR_BUYER_TYPE_OPTIONS } from './consts';
import TableActionButton from '../../../../components/DataTable/TableActionButton';
import EditIcon from '@mui/icons-material/Edit';
import EyeIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { NoDataWithCheck } from '../../components/NoDataWithCheck';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import { CarcinogenicModuleFooter } from '../../components/CarcinogenicModuleFooter';
import { HandleTabChange, TAB_INDEX } from '../../pages';
import { transformProvidersTableItems } from './helpers';
import { useNavigate } from 'react-router-dom';
import styles from '../../../../components/DataTable/styles.module.css';

interface ProvidersTabContentProps {
    handleTabChange: HandleTabChange;
    showCancelCarcinogenicReportAlert: any;
    onlyVisual: boolean;
}

const ProvidersTabContent = ({ handleTabChange, showCancelCarcinogenicReportAlert, onlyVisual }: ProvidersTabContentProps) => {
    const {
        selected,
        open,
        providersTableMenu,
        providersFilters,
        isDeletingProvider,
        providersEmptyDeclarationCheckState,
        translate,
        setOpen,
        toggleProvidersEmptyDeclarationCheck,
        handleClickNewProviderButton,
        handleClickEditProviderButton,
        handleClickDeleteProviderButton
    } = useProvidersModule();

    const navigate = useNavigate();

    const handleFooterCancelClick = () => {
        if (onlyVisual) {
            handleTabChange(TAB_INDEX.SUBSTANCES);
            return;
        }

        showCancelCarcinogenicReportAlert({ confirmAction: () => navigate(-1) });
    };

    const { menuOpen } = providersTableMenu;
    const { filters, setFilters } = providersFilters;

    const carcinogenicReport = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicReport
    );

    const renderProviderOrBuyerType = (providerTypeId: number): string | undefined => {
        const providerOrBuyerType = NEW_PROVIDER_OR_BUYER_TYPE_OPTIONS.find(
            (type: { id: number; descripcion: string }) => type?.id === providerTypeId
        );

        return providerOrBuyerType?.descripcion;
    };
    
    const transformedItems = transformProvidersTableItems(carcinogenicReport?.sustancias || []);



    return (
        <>
            <Box bgcolor={'#ffffff'} borderRadius={'0px 0px 6px 6px'} p={3}>
                {!onlyVisual && (
                    <Grid container xs justifyContent={'flex-end'}>
                        <Button
                            type="contained"
                            onClick={() => {
                                toggleProvidersEmptyDeclarationCheck({ customValue: false });
                                handleClickNewProviderButton();
                            }}
                            loading={false}
                            testID={TEST_IDS.newProviderButton}
                        >
                            {translate('newProviderButton')}
                        </Button>
                    </Grid>
                )}
                <Grid item xs={12} mt={3}>
                    {transformedItems?.length === 0 ? (
                        <NoDataWithCheck
                            title={translate('noData.title')}
                            subtitle={translate('noData.subtitle1')}
                            extraSubtitle={translate('noData.subtitle2')}
                            declareNoDataText={translate('noData.declareNoData')}
                            checkState={providersEmptyDeclarationCheckState}
                            onClickCheck={() => {
                                if (onlyVisual) return;
                                toggleProvidersEmptyDeclarationCheck({});
                            }}
                        />
                    ) : (
                        <TableContainer>
                            <DataTable
                                testID={TEST_IDS.table.id}
                                columnTitles={mainTableColumnTitles(translate)}
                                columnWidths={[]}
                                onSelectRow={() => {}}
                                selected={selected}
                                selectedItemFromAction={null}
                                menuOpen={menuOpen}
                                items={{ data: { data: transformedItems } }}
                                loading={false}
                                filters={filters}
                                setFilters={setFilters}
                                isFetching={false}
                                headers={[
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => `${item?.codigo}`
                                    },
                                    {
                                        type: 'data',
                                        maxWidth: '200px',
                                        upperLabel: (item: any) => `${item?.descripcion || '-'}`
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) =>
                                            `${item?.proveedor?.nombreQuimico || '-'}`
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => `${item?.proveedor?.cuit || '-'}`
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) =>
                                            `${
                                                renderProviderOrBuyerType(item?.proveedor?.tipo) ||
                                                '-'
                                            }`
                                    },
                                    {
                                        type: 'data',
                                        lowerLabel: (item: any) => (
                                            <Box display={'flex'} gap={1.5}>
                                                {!onlyVisual && (
                                                    <TableActionButton
                                                        disabled={false}
                                                        onClick={() =>
                                                            handleClickDeleteProviderButton(item)
                                                        }
                                                        IconComponent={DeleteIcon}
                                                    />
                                                )}
                                                <TableActionButton
                                                    disabled={false}
                                                    onClick={() =>
                                                        handleClickEditProviderButton(item)
                                                    }
                                                    IconComponent={!onlyVisual ? EditIcon : EyeIcon}
                                                />
                                            </Box>
                                        )
                                    }
                                ]}
                            />
                        </TableContainer>
                    )}
                </Grid>
                <DrawerManager
                    open={open}
                    setOpen={setOpen}
                    selectedSubstance={selected}
                    translate={translate}
                    onlyVisual={onlyVisual}
                />
                <BackdropComponent loading={isDeletingProvider} />
            </Box>
            <CarcinogenicModuleFooter
                handleCancelClick={handleFooterCancelClick}
                handleSubmitClick={(e: any) => handleTabChange(e, TAB_INDEX.RADIOLOGICAL_EQUIPMENT)}
                onlyVisual={onlyVisual}
            />
        </>
    );
};

export default ProvidersTabContent;
